import { ref } from 'vue'

interface ConfirmDialogState {
  show: boolean
  title: string
  message: string
  loading: boolean
  confirmText: string
  confirmColor: 'red' | 'primary'
}

const defaultState: ConfirmDialogState = {
  show: false,
  title: '',
  message: '',
  loading: false,
  confirmText: 'Confirm',
  confirmColor: 'red'
}

/**
 * Composable for managing ConfirmDialog state.
 *
 * Usage:
 *   const { confirmDialog, pendingAction, confirm, executeConfirm, cancelConfirm } = useConfirmDialog()
 *
 *   // Trigger confirmation
 *   confirm({
 *     title: 'Delete User',
 *     message: 'Are you sure?',
 *     confirmText: 'Delete',
 *     onConfirm: async () => { await apiDelete(`/users/${id}`) }
 *   })
 *
 *   // In template:
 *   <ConfirmDialog v-bind="confirmDialog" @confirm="executeConfirm" @cancel="cancelConfirm" />
 */
export function useConfirmDialog() {
  const confirmDialog = ref<ConfirmDialogState>({ ...defaultState })
  const pendingAction = ref<(() => Promise<void>) | null>(null)

  function confirm(opts: {
    title: string
    message: string
    confirmText?: string
    confirmColor?: 'red' | 'primary'
    onConfirm: () => Promise<void>
  }) {
    pendingAction.value = opts.onConfirm
    confirmDialog.value = {
      show: true,
      title: opts.title,
      message: opts.message,
      loading: false,
      confirmText: opts.confirmText || 'Confirm',
      confirmColor: opts.confirmColor || 'red'
    }
  }

  async function executeConfirm() {
    if (!pendingAction.value) return
    confirmDialog.value.loading = true
    try {
      await pendingAction.value()
    } finally {
      confirmDialog.value = { ...defaultState }
      pendingAction.value = null
    }
  }

  function cancelConfirm() {
    confirmDialog.value = { ...defaultState }
    pendingAction.value = null
  }

  return {
    confirmDialog,
    pendingAction,
    confirm,
    executeConfirm,
    cancelConfirm
  }
}
